import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-toastify/dist/ReactToastify.css";
import "react-loading-skeleton/dist/skeleton.css";
import PageHeading from "../../PageHeading";
import { useTranslation } from "react-i18next";
import DynamicSelect from "../../DynamicSelect";

/**
 * Example of converting Bengali digits to English digits
 * if you have numeric fields typed in Bangla.
 */
function convertBengaliToEnglish(str) {
  const map = {
    "০": "0",
    "১": "1",
    "২": "2",
    "৩": "3",
    "৪": "4",
    "৫": "5",
    "৬": "6",
    "৭": "7",
    "৮": "8",
    "৯": "9",
  };
  return str.replace(/[০-৯]/g, (digit) => map[digit]);
}

// Helper function to format date to 'YYYY-MM-DD'
function formatDate(dateString) {
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

// Constants
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const EditPatient = ({isSearchable}) => {
  const { id } = useParams(); // patient id from URL
  const { i18n } = useTranslation([]);
  const navigate = useNavigate();

  // Language selection for translations
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [selectedBloodGroup, setSelectedBloodGroup] = useState(null);

  // Patient data shape
  const [patientData, setPatientData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    gender: "",
    bloodGroup: "",
    dateOfBirth: "",
    age: "",
    weight: "",
    image: null, // existing image path from DB
  });

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // If a new image is chosen
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 1) Fetch existing patient data
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchPatient = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(
          `https://api.muktihospital.com/api/patient/${id}`,
          {
            headers: {
              "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079", // Set your API key here
            },
          }
        );

        if (response.status === 200) {
          const patient = response.data;
          setPatientData({
            name: patient.name || "",
            phoneNumber: patient.phoneNumber || "",
            email: patient.email || "",
            gender: patient.gender || "",
            bloodGroup: patient.bloodGroup || "",
            dateOfBirth: patient.dateOfBirth ? formatDate(patient.dateOfBirth) : "", // Format the date
            age: patient.age || "",
            weight: patient.weight || "",
            image: patient.image || null,
          });
        } else {
          toast.error("Failed to load patient details.");
        }
      } catch (error) {
        console.error("Error fetching patient:", error);
        toast.error("Could not load patient.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPatient();
  }, [id]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2) Handle language switch
  // ─────────────────────────────────────────────────────────────────────────────
  const handleLanguageChange = (e) => {
    setSelectedLanguage(e.target.value);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 3) Input field changes
  // ─────────────────────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Convert digits if needed
    const convertedValue = convertBengaliToEnglish(value);

    setPatientData((prev) => ({
      ...prev,
      [name]: convertedValue,
    }));
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4) File input change
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setImageFile(file);

    if (file) {
      // Create a local preview
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

    // Gender select
    const genderOptions = [
      { value: "male", label: "Male" },
      { value: "female", label: "Female" },
      { value: "other", label: "Other" },
    ];

   // Handle blood group change
   const handleBloodGroupChange = (selectedOption) => {
    setSelectedBloodGroup(selectedOption); // Set the selected option
    setPatientData((prev) => ({
      ...prev,
      bloodGroup: selectedOption?.value || "",
    }));
  };
  
  // ─────────────────────────────────────────────────────────────────────────────
  // 5) Submit form
  // ─────────────────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("name", patientData.name);
      formData.append("phoneNumber", patientData.phoneNumber);
      formData.append("email", patientData.email);
      formData.append("gender", patientData.gender);
      formData.append("bloodGroup", patientData.bloodGroup);
      formData.append("dateOfBirth", patientData.dateOfBirth);
      formData.append("age", patientData.age);
      formData.append("weight", patientData.weight);

      // Append image if it's provided
      if (imageFile) {
        formData.append("image", imageFile);
      }

      const response = await axios.put(
        `https://api.muktihospital.com/api/patient/edit/${id}`,
        formData,
        {
          headers: {
            "x-api-key": "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079", // Set your API key here
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.status === 200) {
        toast.success("Patient updated successfully!");
        navigate("/all-patients");  // Redirect to patient list page
      } else {
        toast.error("Failed to update patient.");
      }
    } catch (error) {
      console.error("❌ Error updating patient:", error);
      toast.error("An error occurred while updating the patient.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // If loading
  // ─────────────────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="p-6">
        <Skeleton height={30} count={6} />
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Render form
  // ─────────────────────────────────────────────────────────────────────────────
  const breadcrumbs = [
    { label: 'Dashboard', url: '/' },
    { label: 'Manage Patients'},
    { label: 'Edit Patient' }
  ];
  return (
    <div>
      <PageHeading title="Edit Patient" breadcrumbs={breadcrumbs} />
      <div>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-6 items-start">
          <div className="bg-white shadow-lg rounded-lg">
             <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                  Basic Information
                </h2>
              </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-5">
            {/* Name */}
            <div>
              <label className="label">Patient Name</label>
              {isLoading ? <Skeleton height={40} /> : 
                <input type="text" name="name" value={patientData.name} onChange={handleInputChange} className="input-field" placeholder="Enter Patient Name" />
              }
            </div>

            {/* Phone Number */}
            <div>
              <label className="label">Phone Number</label>
              {isLoading ? <Skeleton height={40} /> : 
                <input type="tel" name="phoneNumber" value={patientData.phoneNumber} onChange={handleInputChange} className="input-field" placeholder="Enter Phone Number" />
              }
            </div>

            {/* Email */}
            <div>
              <label className="label">Email</label>
              {isLoading ? <Skeleton height={40} /> : 
                <input type="email" name="email" value={patientData.email} onChange={handleInputChange} className="input-field" placeholder="Enter Email" />
              }
            </div>

            {/* Gender */}
            <div>
              <label className="label">Gender</label>
              <DynamicSelect
                  name="gender"
                  // Ensure the value corresponds to the selected gender, use the gender from patientData
                  value={genderOptions.find((option) => option.value === patientData.gender) || null}
                  onChange={(selectedOption) => 
                    handleInputChange({
                      target: { name: "gender", value: selectedOption ? selectedOption.value : "" },
                    })
                  }
                  options={genderOptions}
                  placeholder="Select Gender"
                  isSearchable={isSearchable}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
                </div>

            {/* Blood Group */}
                <div>
                  <label className="label">Blood Group</label>
                  <DynamicSelect
                      options={[
                        { value: "A+", label: "A+" },
                        { value: "A-", label: "A-" },
                        { value: "B+", label: "B+" },
                        { value: "B-", label: "B-" },
                        { value: "O+", label: "O+" },
                        { value: "O-", label: "O-" },
                        { value: "AB+", label: "AB+" },
                        { value: "AB-", label: "AB-" },
                      ]}
                      // Pass the value of selectedBloodGroup or fall back to patientData.bloodGroup
                      value={selectedBloodGroup || { value: patientData.bloodGroup, label: patientData.bloodGroup }}
                      onChange={handleBloodGroupChange}
                      placeholder="Select Blood Group"
                      isClearable
                    />
                </div>

            {/* Date of Birth */}
            <div>
              <label className="label">Date of Birth</label>
              {isLoading ? <Skeleton height={40} /> : 
                <input type="date" name="dateOfBirth" value={patientData.dateOfBirth} onChange={handleInputChange} className="input-field" />
              }
            </div>

            {/* Age */}
            <div>
              <label className="label">Age</label>
              {isLoading ? <Skeleton height={40} /> : 
                <input type="number" name="age" value={patientData.age} onChange={handleInputChange} className="input-field" placeholder="Enter Age" />
              }
            </div>

            {/* Weight */}
            <div>
              <label className="label">Weight (kg)</label>
              {isLoading ? <Skeleton height={40} /> : 
                <input type="number" name="weight" value={patientData.weight} onChange={handleInputChange} className="input-field" placeholder="Enter Weight" />
              }
            </div>
          </div>
             </div>

         {/* Profile Image Upload */}
        <div className="bg-white shadow-lg rounded-lg">
             <div className="flex items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                  Upload Profile Photo
                </h2>
              </div>
          <div className="p-5">
           {/* Existing image from DB */}
            {!imageFile && (patientData.image ? (
              <div className="mb-4">
                <img
                  src={`https://api.muktihospital.com${patientData.image}`} 
                  alt="Current Patient"
                  className="w-24 h-24 rounded-full object-cover border shadow"
                />
              </div>
            ) : (
              <div className="mb-4">
                <img
                  src="https://placehold.co/100" // Placeholder image if no image is found
                  alt="Placeholder"
                  className="w-24 h-24 rounded-full object-cover border shadow"
                />
              </div>
            ))}
            {/* File input */}
            <div className="mt-4">
                <p className="text-sm text-gray-600 mt-1">
                  Max file size: {MAX_FILE_SIZE / 1024 / 1024}MB. Allowed types: JPEG, PNG, GIF, WEBP
                </p>
                <input
                  type="file"
                  accept="image/*"
                  className="border p-2 rounded w-full"
                  disabled={isSubmitting}
                  onChange={handleFileChange}
                />
              </div>
          </div>
        </div>

          </div>
          {/* Submit & Discard Buttons */}
          <div className="mt-6 flex gap-4">
            <button type="submit" className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
            <button type="button" onClick={() => navigate("/all-patients")} className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300" disabled={isSubmitting}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditPatient;
